import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ClipboardCheck, Plus, Trash2, GripVertical, Loader2, Eye, EyeOff } from "lucide-react";

export default function CheckQualityConfig() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newLabel, setNewLabel] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/check-quality-items");
      setItems(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const add = async () => {
    const label = newLabel.trim();
    if (!label) return;
    try {
      await api.post("/check-quality-items", { label, order: items.length + 1, active: true });
      setNewLabel("");
      toast.success("Voce aggiunta");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questa voce? Le valutazioni già fatte restano nei clienti.")) return;
    try {
      await api.delete(`/check-quality-items/${id}`);
      toast.success("Voce eliminata");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggle = async (it) => {
    try {
      await api.put(`/check-quality-items/${it.id}`, { label: it.label, order: it.order, active: !it.active });
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const rename = async (it, label) => {
    if (!label.trim() || label === it.label) return;
    try {
      await api.put(`/check-quality-items/${it.id}`, { label, order: it.order, active: it.active });
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="card-shell p-6" data-testid="check-quality-config">
      <div className="flex items-center gap-2 mb-4">
        <ClipboardCheck className="w-5 h-5" style={{ color: "var(--primary)" }} />
        <h2 className="font-bold text-lg" style={{ fontFamily: "Manrope" }}>Configurazione Check Quality</h2>
      </div>
      <p className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>
        Definisci le voci che i tecnici valuteranno (con stelline 1–5) nella scheda Check Quality di ogni cliente.
      </p>

      <div className="flex gap-2 mb-4">
        <input
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
          placeholder="Nuova voce (es. Serraggio morsetti, Test isolamento, Ordine cavi...)"
          className="flex-1 px-3 py-2 text-sm rounded-md border outline-none"
          style={{ borderColor: "var(--border)", background: "#fff" }}
          data-testid="cq-config-new-label"
        />
        <button onClick={add} disabled={!newLabel.trim()} className="btn-primary disabled:opacity-50" data-testid="cq-config-add">
          <Plus className="w-4 h-4" /> Aggiungi
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm p-4"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</div>
      ) : items.length === 0 ? (
        <div className="p-8 text-center rounded-md" style={{ background: "var(--surface-muted)" }}>
          <div className="text-sm" style={{ color: "var(--text-muted)" }}>Nessuna voce configurata</div>
          <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
            Esempi utili: "Sicurezza cantiere", "Pulizia post-installazione", "Test inverter", "Documenti consegnati"
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={it.id} className="flex items-center gap-2 p-2 rounded-md border" style={{ borderColor: "var(--border)", opacity: it.active ? 1 : 0.55 }} data-testid={`cq-config-item-${it.id}`}>
              <GripVertical className="w-4 h-4" style={{ color: "var(--text-muted)" }} />
              <span className="text-xs font-mono w-6 text-center" style={{ color: "var(--text-muted)" }}>{i + 1}</span>
              <input
                defaultValue={it.label}
                onBlur={(e) => rename(it, e.target.value.trim())}
                onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                className="flex-1 px-2 py-1 text-sm rounded border outline-none"
                style={{ borderColor: "transparent", background: "transparent" }}
                data-testid={`cq-config-label-${it.id}`}
              />
              <button onClick={() => toggle(it)} className="p-1.5 rounded hover:bg-black/5" title={it.active ? "Disattiva" : "Attiva"} data-testid={`cq-config-toggle-${it.id}`}>
                {it.active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" style={{ color: "var(--text-muted)" }} />}
              </button>
              <button onClick={() => remove(it.id)} className="p-1.5 rounded hover:bg-black/5" data-testid={`cq-config-delete-${it.id}`}>
                <Trash2 className="w-4 h-4" style={{ color: "#B30000" }} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
